import { useCallback } from 'react';
import { produce } from 'immer';

import useUser from '@/hooks/useUser';
import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

type CamelCase<T> = T extends `${infer U}_${infer V}`
  ? `${U}${Capitalize<CamelCase<V>>}`
  : T;

type CamelCaseKeys<O> = {
  [K in keyof O as CamelCase<K>]: O[K];
};

type MutateUserSettings = NonNullable<
  Parameters<ReturnType<typeof useUser>['mutate']>[0]['settings']
>;

type MutatePortfolioLinkSettings = NonNullable<
  NonNullable<MutateUserSettings['portfolio']>['links']
>[Portfolio['id']];

type Args = {
  portfolioId: Portfolio['id'];
};

export function usePortfolioLinkSettingsMutation({ portfolioId }: Args) {
  const {
    mutationStatus: status,
    mutate: updateUserSettings,
    setQueryData,
  } = useUser();

  const mutate = useCallback(
    (patch: CamelCaseKeys<MutatePortfolioLinkSettings>) => {
      setQueryData((profileState) =>
        produce(profileState, (draft) => {
          if (draft?.portfolio?.links?.[portfolioId]) {
            const portfolioLinksSettings = draft.portfolio.links[portfolioId];
            draft.portfolio.links[portfolioId] = {
              hasDismissedError:
                patch.hasDismissedError ??
                portfolioLinksSettings.hasDismissedError,
              hasDismissedSuccess:
                patch.hasDismissedSuccess ??
                portfolioLinksSettings.hasDismissedSuccess,
            };
          }
          return draft;
        })
      );
      updateUserSettings({
        settings: {
          portfolio: {
            links: {
              [portfolioId]: {
                has_dismissed_error: patch.hasDismissedError,
                has_dismissed_success: patch.hasDismissedSuccess,
              },
            },
          },
        },
      });
    },
    [setQueryData, updateUserSettings, portfolioId]
  );

  return { mutate, status };
}
